'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import { getToolAuthToken } from '@/lib/browser-tool-usage';
import { encodeCanvasInSoftware } from '@/lib/software-image-decode';

type PdfJsViewport = {
  width: number;
  height: number;
};

type PdfJsPage = {
  getViewport: (params: { scale: number }) => PdfJsViewport;
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    canvas: HTMLCanvasElement;
    viewport: PdfJsViewport;
  }) => { promise: Promise<void> };
  cleanup?: () => void;
};

type PdfJsDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfJsPage>;
  cleanup?: () => void;
  destroy?: () => Promise<void> | void;
};

type PdfJsModule = {
  GlobalWorkerOptions: {
    workerSrc: string;
  };
  getDocument: (params: {
    data: Uint8Array;
    disableWorker: boolean;
    useSystemFonts?: boolean;
    isEvalSupported?: boolean;
  }) => {
    promise: Promise<PdfJsDocument>;
  };
};

type PdfToImagesSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

type OutputFormat = 'jpg' | 'png' | 'tiff' | 'bmp' | 'gif';
const OUTPUT_FORMATS: OutputFormat[] = ['jpg', 'png', 'tiff', 'bmp', 'gif'];
type PageMode = 'all-pages' | 'custom-ranges';
type QualityPresetId = 'balanced' | 'clearer';

type ExportProgress = {
  current: number;
  total: number;
  label: string;
};

type PdfSegment = {
  start: number;
  end: number;
};

type QualityPreset = {
  id: QualityPresetId;
  label: string;
  hint: string;
  renderScale: number;
  jpegQuality: number;
};

const maxRenderEdge = 2400;
const maxCanvasPixels = 7_000_000;

const qualityPresets: QualityPreset[] = [
  {
    id: 'balanced',
    label: 'Balanced',
    hint: 'Good for quick sharing and smaller ZIP downloads.',
    renderScale: 1.7,
    jpegQuality: 0.78,
  },
  {
    id: 'clearer',
    label: 'Better quality',
    hint: 'Keeps more detail for dense text, receipts, and diagrams.',
    renderScale: 2.1,
    jpegQuality: 0.9,
  },
];

let pdfJsModulePromise: Promise<PdfJsModule> | null = null;

function loadPdfJsModule() {
  if (!pdfJsModulePromise) {
    pdfJsModulePromise =
      import('pdfjs-dist/legacy/build/pdf.mjs') as unknown as Promise<PdfJsModule>;
  }

  return pdfJsModulePromise.then((module) => {
    if (!module.GlobalWorkerOptions.workerSrc) {
      module.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
    }

    return module;
  });
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatExportTimestamp(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

function createZipFileName(format: OutputFormat, imageCount: number) {
  return `cleanor.app-pdf-to-images-${imageCount}-${format}-${formatExportTimestamp(new Date())}.zip`;
}

function createImageFileName(pageNumber: number, digits: number, format: OutputFormat) {
  const page = String(pageNumber).padStart(digits, '0');
  return `cleanor.app-pdf-to-images-page-${page}.${format}`;
}

function triggerBrowserDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function isPdfFile(file: File) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

function getRenderScale(viewport: PdfJsViewport, preferredScale: number) {
  const basePixels = viewport.width * viewport.height;
  const edgeScale = maxRenderEdge / Math.max(viewport.width, viewport.height);
  const pixelScale = Math.sqrt(maxCanvasPixels / basePixels);

  return Math.max(1, Math.min(preferredScale, edgeScale, pixelScale));
}

function buildEveryPageSegments(pageCount: number): PdfSegment[] {
  return Array.from({ length: pageCount }, (_, index) => ({
    start: index + 1,
    end: index + 1,
  }));
}

function parseCustomSegments(input: string, pageCount: number): PdfSegment[] {
  const trimmed = input.trim();

  if (!trimmed) {
    throw new Error('Enter page ranges like 1-3, 4-6, or 8.');
  }

  const parts = trimmed
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) {
    throw new Error('Enter at least one page range.');
  }

  const segments: PdfSegment[] = [];
  const usedPages = new Set<number>();

  for (const part of parts) {
    const rangeMatch = part.match(/^(\d+)\s*-\s*(\d+)$/);
    const singleMatch = part.match(/^(\d+)$/);

    let start = 0;
    let end = 0;

    if (rangeMatch) {
      start = Number(rangeMatch[1]);
      end = Number(rangeMatch[2]);
    } else if (singleMatch) {
      start = Number(singleMatch[1]);
      end = start;
    } else {
      throw new Error(`"${part}" is not a valid page range.`);
    }

    if (start < 1 || end < 1 || start > pageCount || end > pageCount) {
      throw new Error(`"${part}" is outside the page count for this PDF.`);
    }

    if (start > end) {
      throw new Error(`"${part}" is reversed. Put the smaller page first.`);
    }

    for (let page = start; page <= end; page += 1) {
      if (usedPages.has(page)) {
        throw new Error(`Page ${page} is included more than once. Use non-overlapping ranges.`);
      }

      usedPages.add(page);
    }

    segments.push({ start, end });
  }

  return segments;
}

function expandSegments(segments: PdfSegment[]) {
  return segments.flatMap((segment) =>
    Array.from({ length: segment.end - segment.start + 1 }, (_, index) => segment.start + index),
  );
}

async function canvasToBlob(canvas: HTMLCanvasElement, format: OutputFormat, jpegQuality: number) {
  if (format === 'tiff' || format === 'bmp' || format === 'gif') {
    return encodeCanvasInSoftware(canvas, format);
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('The PDF page could not be converted into an image.'));
      },
      format === 'png' ? 'image/png' : 'image/jpeg',
      format === 'png' ? undefined : jpegQuality,
    );
  });
}

async function createPreviewUrl(pdfDocument: PdfJsDocument) {
  const page = await pdfDocument.getPage(1);

  try {
    const baseViewport = page.getViewport({ scale: 1 });
    const previewScale = Math.max(
      0.7,
      Math.min(1.05, 280 / Math.max(baseViewport.width, baseViewport.height)),
    );
    const viewport = page.getViewport({ scale: previewScale });
    const canvas = window.document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));

    const context = canvas.getContext('2d');

    if (!context) {
      return null;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, canvas, viewport }).promise;
    const blob = await canvasToBlob(canvas, 'jpg', 0.84);
    canvas.width = 0;
    canvas.height = 0;
    return URL.createObjectURL(blob);
  } finally {
    page.cleanup?.();
  }
}

async function readSource(file: File): Promise<PdfToImagesSource> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdfjs = await loadPdfJsModule();
  const loadingTask = pdfjs.getDocument({
    data: bytes,
    disableWorker: true,
    useSystemFonts: true,
    isEvalSupported: false,
  });

  let pdfDocument: PdfJsDocument | null = null;

  try {
    pdfDocument = await loadingTask.promise;

    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: pdfDocument.numPages,
      previewUrl: await createPreviewUrl(pdfDocument),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    pdfDocument?.cleanup?.();
    await pdfDocument?.destroy?.();
  }
}

export default function BrowserPdfToImages({
  defaultFormat = 'jpg',
  heading,
  toolSlug = 'pdf-to-images',
}: { defaultFormat?: OutputFormat; heading?: string; toolSlug?: string } = {}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [source, setSource] = useState<PdfToImagesSource | null>(null);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(defaultFormat);
  const [pageMode, setPageMode] = useState<PageMode>('all-pages');
  const [customRanges, setCustomRanges] = useState('1-3');
  const [qualityPreset, setQualityPreset] = useState<QualityPresetId>('balanced');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [resultImageCount, setResultImageCount] = useState<number | null>(null);

  const activeQualityPreset =
    qualityPresets.find((preset) => preset.id === qualityPreset) ?? qualityPresets[0];

  const plannedSegments = useMemo(() => {
    if (!source) {
      return [] as PdfSegment[];
    }

    try {
      return pageMode === 'all-pages'
        ? buildEveryPageSegments(source.pageCount)
        : parseCustomSegments(customRanges, source.pageCount);
    } catch {
      return [] as PdfSegment[];
    }
  }, [customRanges, pageMode, source]);

  const plannedPages = useMemo(() => expandSegments(plannedSegments), [plannedSegments]);

  useEffect(() => {
    return () => {
      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
      }

      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }
    };
  }, [source]);

  function resetExportState() {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
    }

    downloadUrlRef.current = null;
    setResultSize(null);
    setResultImageCount(null);
    setProgress(null);
  }

  function replaceSource(nextSource: PdfToImagesSource | null) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one PDF file to export as images.');
      return;
    }

    try {
      const nextSource = await readSource(file);
      replaceSource(nextSource);
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  function clearSource() {
    replaceSource(null);
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function exportImages() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    let segments: PdfSegment[] = [];

    try {
      segments =
        pageMode === 'all-pages'
          ? buildEveryPageSegments(source.pageCount)
          : parseCustomSegments(customRanges, source.pageCount);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Check the page ranges and try again.',
      );
      return;
    }

    const pageNumbers = expandSegments(segments);

    if (!pageNumbers.length) {
      setErrorMessage('Choose at least one page to export.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setProgress({ current: 0, total: pageNumbers.length, label: 'Preparing PDF pages…' });

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another PDF export right now.';
        throw new Error(message);
      }

      const pdfjs = await loadPdfJsModule();
      const bytes = new Uint8Array(await source.file.arrayBuffer());
      const loadingTask = pdfjs.getDocument({
        data: bytes,
        disableWorker: true,
        useSystemFonts: true,
        isEvalSupported: false,
      });

      let pdfDocument: PdfJsDocument | null = null;

      try {
        pdfDocument = await loadingTask.promise;
        const zip = new JSZip();
        const digits = String(source.pageCount).length;

        for (let index = 0; index < pageNumbers.length; index += 1) {
          const pageNumber = pageNumbers[index];

          setProgress({
            current: index + 1,
            total: pageNumbers.length,
            label: `Rendering page ${pageNumber} of ${source.pageCount}…`,
          });

          const page = await pdfDocument.getPage(pageNumber);

          try {
            const baseViewport = page.getViewport({ scale: 1 });
            const viewport = page.getViewport({
              scale: getRenderScale(baseViewport, activeQualityPreset.renderScale),
            });
            const canvas = window.document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(viewport.width));
            canvas.height = Math.max(1, Math.round(viewport.height));

            const context = canvas.getContext('2d');

            if (!context) {
              throw new Error('Your browser could not render the PDF page.');
            }

            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            await page.render({ canvasContext: context, canvas, viewport }).promise;
            const blob = await canvasToBlob(canvas, outputFormat, activeQualityPreset.jpegQuality);
            zip.file(createImageFileName(pageNumber, digits, outputFormat), blob);
            canvas.width = 0;
            canvas.height = 0;
          } finally {
            page.cleanup?.();
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const nextDownloadUrl = URL.createObjectURL(zipBlob);
        const nextDownloadName = createZipFileName(outputFormat, pageNumbers.length);

        resetExportState();
        downloadUrlRef.current = nextDownloadUrl;
        setResultSize(zipBlob.size);
        setResultImageCount(pageNumbers.length);
        triggerBrowserDownload(nextDownloadUrl, nextDownloadName);
        setCreditMessage(
          usagePayload.data?.credits?.plan === 'pro'
            ? 'PDF image export download started. Unlimited plan active.'
            : usagePayload.data?.credits?.remaining !== undefined
              ? `PDF image export download started. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
              : 'PDF image export download started.',
        );
      } finally {
        pdfDocument?.cleanup?.();
        await pdfDocument?.destroy?.();
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The PDF could not be exported as images.',
      );
    } finally {
      setIsExporting(false);
      setProgress(null);
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>{heading ?? 'Turn one PDF into image files'}</h2>
          <p>
            Upload one PDF, choose JPG or PNG output, export every page or selected ranges, and
            download the result as one clean ZIP file without sending the document to a server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-routes" aria-label="Output format">
        {OUTPUT_FORMATS.map((format) => (
          <button
            key={format}
            className={`tool-live-route ${outputFormat === format ? 'tool-live-route-active' : ''}`}
            onClick={() => {
              setOutputFormat(format);
              setErrorMessage(null);
              setCreditMessage(null);
              resetExportState();
            }}
            type="button"
          >
            {format.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source
                  ? `${source.pageCount} page${source.pageCount === 1 ? '' : 's'} ready to export`
                  : 'Choose one PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${formatFileSize(source.size)} source size • ${plannedPages.length || 0} image file${plannedPages.length === 1 ? '' : 's'} planned`
                  : 'Works best with standard unlocked PDF files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-routes" aria-label="Page export mode">
            <button
              className={`tool-live-route ${pageMode === 'all-pages' ? 'tool-live-route-active' : ''}`}
              onClick={() => {
                setPageMode('all-pages');
                setErrorMessage(null);
                setCreditMessage(null);
                resetExportState();
              }}
              type="button"
            >
              All pages
            </button>
            <button
              className={`tool-live-route ${pageMode === 'custom-ranges' ? 'tool-live-route-active' : ''}`}
              onClick={() => {
                setPageMode('custom-ranges');
                setErrorMessage(null);
                setCreditMessage(null);
                resetExportState();
              }}
              type="button"
            >
              Custom ranges
            </button>
          </div>

          {pageMode === 'custom-ranges' ? (
            <label className="field">
              <span>Page ranges</span>
              <input
                onChange={(event) => {
                  setCustomRanges(event.target.value);
                  setErrorMessage(null);
                  setCreditMessage(null);
                  resetExportState();
                }}
                placeholder="1-3, 5, 7-9"
                type="text"
                value={customRanges}
              />
              <small>Use commas between ranges. Example: 1-3, 5, 7-9</small>
            </label>
          ) : null}

          {outputFormat === 'jpg' ? (
            <div className="tool-live-routes" aria-label="Image quality">
              {qualityPresets.map((preset) => (
                <button
                  key={preset.id}
                  className={`tool-live-route ${qualityPreset === preset.id ? 'tool-live-route-active' : ''}`}
                  onClick={() => {
                    setQualityPreset(preset.id);
                    setErrorMessage(null);
                    setCreditMessage(null);
                    resetExportState();
                  }}
                  type="button"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || !plannedPages.length || isExporting}
              onClick={() => void exportImages()}
              type="button"
            >
              {isExporting ? 'Exporting images…' : `Export ${outputFormat.toUpperCase()}`}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {progress ? (
            <div className="tool-live-panel-note tool-pdf-progress" aria-live="polite">
              <strong>{progress.label}</strong>
              <progress max={100} value={Math.round((progress.current / progress.total) * 100)} />
              <span>{`${progress.current} / ${progress.total}`}</span>
            </div>
          ) : null}

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Client-side PDF image exporter</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → Images</span>
            <span>{outputFormat.toUpperCase()}</span>
            <span>{pageMode === 'all-pages' ? 'All pages' : 'Custom ranges'}</span>
            <span>ZIP export</span>
          </div>
          <p>
            {outputFormat === 'png'
              ? 'PNG keeps pages lossless and is useful when image clarity matters more than ZIP size.'
              : activeQualityPreset.hint}
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              Use standard unlocked PDFs. Password-protected files are not supported here.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              <div>
                <strong>Output</strong>
                <span>{outputFormat.toUpperCase()}</span>
              </div>
              <div>
                <strong>Planned images</strong>
                <span>{plannedPages.length || 0}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>ZIP size</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              {resultImageCount ? (
                <div>
                  <strong>Last export</strong>
                  <span>{`${resultImageCount} image file${resultImageCount === 1 ? '' : 's'}`}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source?.previewUrl ? (
        <div className="tool-live-preview">
          <h3>First page preview</h3>
          <div className="tool-live-preview-frame">
            <img alt={`${source.name} first page`} src={source.previewUrl} />
          </div>
        </div>
      ) : null}

      {source ? (
        <div className="tool-live-preview tool-pdf-preview">
          <h3>Planned outputs</h3>
          <div className="tool-pdf-page-list">
            {plannedPages.length ? (
              plannedPages.map((pageNumber, index) => (
                <article className="tool-pdf-page-card" key={pageNumber}>
                  <div className="tool-pdf-page-thumb tool-pdf-file-thumb">
                    <strong>{pageNumber}</strong>
                    <span>page</span>
                  </div>
                  <div className="tool-pdf-page-copy">
                    <strong>{`Output ${index + 1}: page ${pageNumber}`}</strong>
                    <span>{`Exports as ${outputFormat.toUpperCase()} inside one ZIP download`}</span>
                    <span>
                      {createImageFileName(
                        pageNumber,
                        String(source.pageCount).length,
                        outputFormat,
                      )}
                    </span>
                  </div>
                </article>
              ))
            ) : (
              <article className="tool-pdf-page-card">
                <div className="tool-pdf-page-thumb tool-pdf-file-thumb">
                  <strong>?</strong>
                  <span>page</span>
                </div>
                <div className="tool-pdf-page-copy">
                  <strong>Check the ranges before export</strong>
                  <span>Use values like 1-3, 5, 7-9 inside the current page count.</span>
                  <span>The ZIP export will start after the selection is valid.</span>
                </div>
              </article>
            )}
          </div>
        </div>
      ) : null}
    </article>
  );
}
